import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import classnames from 'classnames';

const DOCUMENT_ACCEPT_TYPES = ['.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx', '.pdf', '.txt'];
const maxAttachmentSizeInKiloBytes = sizeInKilobytes => sizeInKilobytes * 1024;
const maxAttachmentSizeInBytes = sizeInMegabytes => maxAttachmentSizeInKiloBytes(sizeInMegabytes) * 1024;
const Errors = {
  CANNOT_UPLOAD_THIS_FILE: 'Cannot upload this file',
  MAXIMUL_FILE_SIZE: (maxFileSize = 1) => `Maximum file size is ${maxFileSize}MB`,
  FILE_ALREADY_UPLOADED: 'This file was already uploaded',
  FILE_READING_ABORTED: 'File reading was aborted',
  FILE_READING_FAILED: 'File reading has failed',
};

class Attachments extends PureComponent {
  static propTypes = {
    acceptTypes: PropTypes.arrayOf(PropTypes.string),
    addFileToForm: PropTypes.func,
    getRawFile: PropTypes.func,
    required: PropTypes.bool,
    headerText: PropTypes.string,
    horizontal: PropTypes.bool,
    disabled: PropTypes.bool,
    downloadTemplateButton: PropTypes.element,
    maxFileSize: PropTypes.number,
    files: PropTypes.arrayOf(PropTypes.shape({
      fileName: PropTypes.string.isRequired,
    })).isRequired,
  };

  static defaultProps = {
    acceptTypes: DOCUMENT_ACCEPT_TYPES,
    maxFileSize: 0,
    headerText: 'Attachments',
    horizontal: false,
    disabled: false,
    customAttachmentDescription: null,
    getRawFile: null,
    files: [],
  };

  addFile = async files => {
    const {
      maxFileSize,
      acceptTypes,
      getRawFile,
    } = this.props;

    this.setState({ error: '' });

    const file = files[0];

    if (!file) {
      this.setState({ error: Errors.CANNOT_UPLOAD_THIS_FILE });
      return;
    }

    if (maxFileSize && file.size > maxAttachmentSizeInBytes(maxFileSize)) {
      this.setState({ error: Errors.MAXIMUL_FILE_SIZE(maxFileSize) });
      return;
    }

    const fileExtension = file.name.split('.').pop();
    if (!acceptTypes.includes(`.${fileExtension}`)) {
      this.setState({ error: Errors.CANNOT_UPLOAD_THIS_FILE });
      return;
    }

    if (this.props.files.find(({ fileName }) => file.name === fileName)) {
      this.setState({ error: Errors.FILE_ALREADY_UPLOADED });
      return;
    }

    if (!getRawFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataUrl = reader.result;

        this.props.addFileToForm({
          fileName: file.name,
          fileType: fileExtension,
          fileImage: fileAsDataUrl.split('base64,')[1],
          fileUrl: fileAsDataUrl,
        });
      };
      reader.onabort = () => this.setState({ error: Errors.FILE_READING_ABORTED });
      reader.onerror = () => this.setState({ error: Errors.FILE_READING_FAILED });

      reader.readAsDataURL(file);
    } else {
      getRawFile(file);
    }
  };

  render() {
    const {
      acceptTypes,
      horizontal,
      required,
      downloadTemplateButton,
      disabled,
    } = this.props;
    const acceptTypesText = acceptTypes.join(', ');

    const attachmenClassName = classnames('attachments', { 'row align-items-center': horizontal } );
    const labelClassName = classnames({ 'col-3': horizontal });
    const dropzoneClassName = classnames({ 'col-9': horizontal });
    const clickToSelectClassName = classnames('attachment-upload-hint', { disabled });

    return (
      <div className={attachmenClassName}>
        <div className={labelClassName}>
          {this.props.headerText}{required ? <span className="form-asterisk"> *</span> : ''}
        </div>
        <div className={dropzoneClassName}>
          <Dropzone
            accept={acceptTypesText}
            onDrop={this.addFile}
          >
            {({ getRootProps, getInputProps }) => (
              <div className="attachment-upload" onDrop={getRootProps().onDrop}>
                Drop a file here, or
                <div {...getRootProps()} className={'link-attachment-wrapper'}>
                  <input {...getInputProps()} disabled={disabled} />
                  <span className={clickToSelectClassName}><span>{' click to select '}</span></span>
                </div>
                {'a file to upload. If necessary, '}
                {downloadTemplateButton}
                {' the import template.'}
              </div>
            )}
          </Dropzone>
        </div>
      </div>
    );
  }
}

export default Attachments;
